import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  CircularProgress,
  MenuItem,
  Select,
  Chip,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudQueue as WeatherIcon,
  Link as LinkIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Add as AddIcon,
  Public as PublicIcon,
  LocationCity as LocationCityIcon,
  DirectionsBusFilled as TransitIcon,
  AltRoute as HighwayIcon,
  NotificationsActive as NotificationsActiveIcon,
  LocalPolice as LocalPoliceIcon,
  LocalHospital as LocalHospitalIcon,
  Business as BusinessIcon,
  RssFeed as RssFeedIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { Article } from '../../../types/article.types';
import {
  fetchArticlesByPublisher,
  deleteArticle,
  updateArticle,
  setPage,
  setRowsPerPage,
  setSortConfig,
  addArticles
} from '../slices/articlesSlice';
import ConfirmDialog from './common/ConfirmDialog';
import CollapsibleSection from '../../../components/common/CollapsibleSection';
import ArticleFilters from './ArticleFilters';
import ArticleForm from './ArticleForm';
import {
  createArticle,
  generateWeatherArticle,
  generateArticleFromUrl,
  generateArticlesFromMultiSource,
  generateArticlesFromPdf,
} from '../api';
import { getWebsitesByPublisher } from '../../websites/api';
import {
  handleMarinCountyClick,
  handleSonomaCountyClick,
  handleSanRafaelClick,
  handleSantaRosaClick,
  handlePetalumaClick,
  handleNovatoClick,
  handleMillValleyClick,
  handle511Click,
  handleGoldenGateBridgeDistrictClick,
  handleSmartClick,
  handleCaltransDistrict4Click,
  handleChpIncidentFeedsClick,
  handlePgeOutagesClick,
  handleAlertMarinClick,
  handleSoCoAlertClick,
  handleNixleClick,
  handleMarinOesPortalClick,
  handleSoCoEmergencyClick,
  handleNwsBayAreaClick,
  handleBaaqmdClick,
  handleMarinHhsClick,
  handleSonomaDhsClick,
  handleSonomaEdbClick,
  handleOpenRssFeedDialog,
} from './ArticlesTableSourceActions';

interface ArticlesTableProps {
  publisherId?: string;
}

// Article status options for dropdown
const STATUS_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'test', label: 'Test' },
  { value: 'offline', label: 'Offline' },
];

const ALWAYS_VISIBLE_SOURCE_KEYS = ['blank', 'from_url', 'from_pdf'] as const;

const formatDateTimeLocalInputValue = (date: Date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const isPdfFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  return file.type === 'application/pdf' || lowerName.endsWith('.pdf');
};

// Central configuration for all Podcast Source buttons
const SOURCE_BUTTONS: {
  key: string;
  label: string;
  icon: React.ReactNode;
}[] = [
  { key: 'blank', label: 'Blank', icon: <AddIcon /> },
  { key: 'from_url', label: 'From URL', icon: <LinkIcon /> },
  { key: 'from_pdf', label: 'From PDF', icon: <PictureAsPdfIcon /> },
  { key: 'weather', label: 'Weather', icon: <WeatherIcon /> },
  { key: 'events', label: 'Events', icon: <NotificationsActiveIcon /> },
  { key: 'from_rss_feed', label: 'From RSS Feed', icon: <RssFeedIcon /> },
  { key: 'marin', label: 'Marin County', icon: <PublicIcon /> },
  { key: 'sonoma', label: 'Sonoma County', icon: <PublicIcon /> },
  { key: 'san_rafael', label: 'San Rafael', icon: <LocationCityIcon /> },
  { key: 'santa_rosa', label: 'Santa Rosa', icon: <LocationCityIcon /> },
  { key: 'petaluma', label: 'Petaluma', icon: <LocationCityIcon /> },
  { key: 'novato', label: 'Novato', icon: <LocationCityIcon /> },
  { key: 'mill_valley', label: 'Mill Valley', icon: <LocationCityIcon /> },
  { key: '511', label: '511.org', icon: <TransitIcon /> },
  { key: 'ggbhtd', label: 'Golden Gate Bridge Hwy & Transit', icon: <TransitIcon /> },
  { key: 'smart', label: 'Sonoma Marin Area Rail Transit', icon: <TransitIcon /> },
  { key: 'caltrans4', label: 'Caltrans District 4 - State Highways', icon: <HighwayIcon /> },
  { key: 'chp', label: 'CHP Incident Feeds', icon: <LocalPoliceIcon /> },
  { key: 'pge', label: "PG&E - Outage Alerts & PSPS", icon: <NotificationsActiveIcon /> },
  { key: 'alert_marin', label: 'AlertMarin County Emergency Alerts', icon: <NotificationsActiveIcon /> },
  { key: 'soco_alert', label: 'SoCoAlert County Emergency Alerts', icon: <NotificationsActiveIcon /> },
  { key: 'nixle', label: 'Nixle Community Alerts', icon: <NotificationsActiveIcon /> },
  { key: 'marin_oes', label: 'Marin County Sheriff - OES Portal', icon: <LocalPoliceIcon /> },
  { key: 'soco_emergency', label: 'SoCoEmergency', icon: <NotificationsActiveIcon /> },
  { key: 'nws_bay', label: 'National Weather Service Bay Area', icon: <WeatherIcon /> },
  { key: 'baaqmd', label: 'Bay Area Air Quality Mgmt District', icon: <PublicIcon /> },
  { key: 'marin_hhs', label: 'Marin County HHS - Public Health', icon: <LocalHospitalIcon /> },
  { key: 'sonoma_dhs', label: 'Sonoma County DHS - Public Health', icon: <LocalHospitalIcon /> },
  { key: 'sonoma_edb', label: 'Sonoma County Economic Development Board', icon: <BusinessIcon /> },
];

const ArticlesTable: React.FC<ArticlesTableProps> = ({ publisherId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filteredItems, isLoading, pagination, sortConfig } = useSelector((state: RootState) => state.articles);

  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);

  // Inline editing state
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [statusValue, setStatusValue] = useState('');

  // ArticleForm state
  const [openForm, setOpenForm] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);

  // Weather article generation state
  const [isGeneratingWeatherArticle, setIsGeneratingWeatherArticle] = useState(false);
  const [weatherArticleError, setWeatherArticleError] = useState<string | null>(null);

  // Article from URL generation state
  const [isGeneratingFromUrl, setIsGeneratingFromUrl] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [articleUrl, setArticleUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const urlInputRef = React.useRef<HTMLInputElement>(null);

  // Article from PDF generation state
  const [isGeneratingFromPdf, setIsGeneratingFromPdf] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null);
  const [selectedPdfFileName, setSelectedPdfFileName] = useState('');
  const [pdfPublishDate, setPdfPublishDate] = useState(formatDateTimeLocalInputValue(new Date()));
  const [pdfDialogError, setPdfDialogError] = useState<string | null>(null);
  const pdfFileInputRef = React.useRef<HTMLInputElement>(null);
  const pdfPublishDateInputRef = React.useRef<HTMLInputElement>(null);

  // Multi-source scanning state
  const [isScanningSources, setIsScanningSources] = useState(false);

  const [sourceFeedbackMessage, setSourceFeedbackMessage] = useState<string | null>(null);
  const [sourceFeedbackSeverity, setSourceFeedbackSeverity] = useState<'success' | 'info' | 'error'>('info');

  // --- New state for dynamic Podcast Sources ---
  const [visibleSourceKeys, setVisibleSourceKeys] = useState<string[]>([...ALWAYS_VISIBLE_SOURCE_KEYS]);
  const [addSourcesDialogOpen, setAddSourcesDialogOpen] = useState(false);

  const LOCAL_STORAGE_KEY = 'podcast-visible-sources';

  // Load visible sources from localStorage on mount
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setVisibleSourceKeys(Array.from(new Set([...ALWAYS_VISIBLE_SOURCE_KEYS, ...parsed])));
        }
      }
    } catch {
      // ignore storage errors and use defaults
    }
  }, []);

  const persistVisibleSources = (keys: string[]) => {
    const next = Array.from(new Set([...ALWAYS_VISIBLE_SOURCE_KEYS, ...keys]));
    setVisibleSourceKeys(next);
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const handleOpenAddSourcesDialog = () => {
    setAddSourcesDialogOpen(true);
  };

  const handleCloseAddSourcesDialog = () => {
    setAddSourcesDialogOpen(false);
  };

  const handleToggleSourceVisibility = (key: string, checked: boolean) => {
    if (ALWAYS_VISIBLE_SOURCE_KEYS.includes(key as typeof ALWAYS_VISIBLE_SOURCE_KEYS[number])) {
      return;
    }

    setVisibleSourceKeys((prev) => {
      let next: string[];
      if (checked) {
        next = prev.includes(key) ? prev : [...prev, key];
      } else {
        next = prev.filter((k) => k !== key);
      }
      next = Array.from(new Set([...ALWAYS_VISIBLE_SOURCE_KEYS, ...next]));
      try {
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleRemoveVisibleSource = (key: string, event: React.MouseEvent) => {
    event.stopPropagation();
    persistVisibleSources(visibleSourceKeys.filter((k) => k !== key));
  };

  const handleEventsClick = () => {
    setSourceFeedbackSeverity('info');
    setSourceFeedbackMessage('Events source is not implemented yet.');
  };

  // Fetch articles when component mounts
  useEffect(() => {
    if (publisherId) {
      dispatch(fetchArticlesByPublisher(publisherId));
    }
  }, [publisherId, dispatch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleRequestSort = (property: string) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    dispatch(setSortConfig({
      key: property,
      direction: isAsc ? 'desc' : 'asc',
    }));

    // Sort the data
    let sortedData = [...filteredItems];
    sortedData.sort((a, b) => {
      if (property === 'publishDate') {
        const dateA = new Date(a[property]).getTime();
        const dateB = new Date(b[property]).getTime();
        return (isAsc ? 1 : -1) * (dateA - dateB);
      } else {
        const valueA = String(a[property as keyof Article]).toLowerCase();
        const valueB = String(b[property as keyof Article]).toLowerCase();
        return (isAsc ? 1 : -1) * valueA.localeCompare(valueB);
      }
    });
  };

  // Editing handlers
  const handleStatusClick = (article: Article, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setEditingStatusId(article.id);
    setStatusValue(article.status);
  };
  const handleStatusChange = async (article: Article, value: string) => {
    setStatusValue(value);
    if (value !== article.status) {
      await dispatch(updateArticle({ id: article.id, data: { status: value } }));
    }
    setEditingStatusId(null);
  };

  // Delete handlers
  const handleDeleteClick = (article: Article, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setArticleToDelete(article);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (articleToDelete) {
      try {
        await dispatch(deleteArticle(articleToDelete.id));
        setOpenConfirmDelete(false);
        setArticleToDelete(null);
      } catch (error) {
        console.error('Error deleting article:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setArticleToDelete(null);
  };

  // ArticleForm handlers
  const handleOpenForm = (article?: Article) => {
    setSelectedArticle(article);
    setIsEdit(!!article);
    setOpenForm(true);
  };
  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedArticle(undefined);
  };
  const handleSaveArticle = async (data: Partial<Article>) => {
    if (isEdit && selectedArticle) {
      await dispatch(updateArticle({ id: selectedArticle.id, data }));
    } else {
      await createArticle(data);
      if (publisherId) {
        dispatch(fetchArticlesByPublisher(publisherId));
      }
    }
  };

  // Handle row click to open editor
  const handleRowClick = (article: Article) => {
    handleOpenForm(article);
  };

  // Format date for display (in the user's local timezone)
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid date';

    // Explicitly format using the user's resolved local time zone.
    // (Intl defaults to local time zone, but being explicit prevents surprises.)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Get tag chip color
  const getTagColor = (index: number) => {
    const colors = ['primary', 'secondary', 'success', 'info', 'warning'];
    return colors[index % colors.length];
  };

  // Get status chip color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'primary';
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Get currently displayed items based on pagination
  const displayedItems = filteredItems.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  );

  // Generate weather article
  const handleGenerateWeatherArticle = async () => {
    setIsGeneratingWeatherArticle(true);
    setWeatherArticleError(null);
    try {
      const websites = await getWebsitesByPublisher(publisherId!);
      if (websites.length === 0) {
        throw new Error('No websites found for this publisher.');
      }
      const websiteId = websites[0].id;
      await generateWeatherArticle(websiteId);
      dispatch(fetchArticlesByPublisher(publisherId!));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate weather article';
      setWeatherArticleError(errorMessage);
    } finally {
      setIsGeneratingWeatherArticle(false);
    }
  };

  // Open URL dialog
  const handleOpenUrlDialog = () => {
    setArticleUrl('');
    setUrlError(null);
    setUrlDialogOpen(true);
    // Focus the input field after dialog opens
    setTimeout(() => {
      urlInputRef.current?.focus();
    }, 100);
  };

  // Close URL dialog
  const handleCloseUrlDialog = () => {
    setUrlDialogOpen(false);
    setArticleUrl('');
    setUrlError(null);
  };

  // Generate article from URL
  const handleGenerateFromUrl = async () => {
    if (!articleUrl.trim()) {
      setUrlError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(articleUrl);
    } catch {
      setUrlError('Please enter a valid URL');
      return;
    }

    setIsGeneratingFromUrl(true);
    setUrlError(null);
    try {
      const websites = await getWebsitesByPublisher(publisherId!);
      if (websites.length === 0) {
        throw new Error('No websites found for this publisher.');
      }
      const websiteId = websites[0].id;
      await generateArticleFromUrl(websiteId, articleUrl);
      dispatch(fetchArticlesByPublisher(publisherId!));
      handleCloseUrlDialog();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate article from URL';
      setUrlError(errorMessage);
    } finally {
      setIsGeneratingFromUrl(false);
    }
  };

  useEffect(() => {
    if (!pdfDialogOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      pdfPublishDateInputRef.current?.focus();
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [pdfDialogOpen]);

  const resetPdfSelection = () => {
    setSelectedPdfFile(null);
    setSelectedPdfFileName('');
    setPdfPublishDate(formatDateTimeLocalInputValue(new Date()));
    setPdfDialogError(null);
    if (pdfFileInputRef.current) {
      pdfFileInputRef.current.value = '';
    }
  };

  const handleOpenPdfPicker = () => {
    setPdfDialogError(null);
    pdfFileInputRef.current?.click();
  };

  const handlePdfFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      if (pdfFileInputRef.current) {
        pdfFileInputRef.current.value = '';
      }
      return;
    }

    if (!isPdfFile(file)) {
      setSourceFeedbackSeverity('error');
      setSourceFeedbackMessage('Please select a PDF file.');
      if (pdfFileInputRef.current) {
        pdfFileInputRef.current.value = '';
      }
      return;
    }

    setSelectedPdfFile(file);
    setSelectedPdfFileName(file.name);
    setPdfPublishDate(formatDateTimeLocalInputValue(new Date()));
    setPdfDialogError(null);
    setPdfDialogOpen(true);
  };

  const handleClosePdfDialog = () => {
    if (isGeneratingFromPdf) {
      return;
    }
    setPdfDialogOpen(false);
    resetPdfSelection();
  };

  const handleGenerateFromPdf = async () => {
    if (!selectedPdfFile) {
      setPdfDialogError('Please choose a PDF file.');
      return;
    }

    if (!pdfPublishDate) {
      setPdfDialogError('Please choose a publish date and time.');
      return;
    }

    const parsedPublishDate = new Date(pdfPublishDate);
    if (Number.isNaN(parsedPublishDate.getTime())) {
      setPdfDialogError('Please choose a valid publish date and time.');
      return;
    }

    setIsGeneratingFromPdf(true);
    setPdfDialogError(null);
    setPdfDialogOpen(false);
    setSourceFeedbackSeverity('info');
    setSourceFeedbackMessage(`Importing "${selectedPdfFile.name}" from PDF. This can take a while.`);

    try {
      if (!publisherId) {
        throw new Error('Publisher ID is missing.');
      }

      const websites = await getWebsitesByPublisher(publisherId);
      if (websites.length === 0) {
        throw new Error('No websites found for this publisher.');
      }

      const websiteId = websites[0].id;
      const response = await generateArticlesFromPdf(
        websiteId,
        selectedPdfFile,
        parsedPublishDate.toISOString()
      );

      await dispatch(fetchArticlesByPublisher(publisherId));

      setSourceFeedbackSeverity('success');
      setSourceFeedbackMessage(
        `Imported "${response.fileName}": ${response.createdCount} created, ${response.duplicateCount} duplicates, ${response.errorCount} errors.`
      );
      resetPdfSelection();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to import PDF';
      setPdfDialogError(errorMessage);
      setSourceFeedbackSeverity('error');
      setSourceFeedbackMessage(errorMessage);
      setPdfDialogOpen(true);
    } finally {
      setIsGeneratingFromPdf(false);
    }
  };

  // Scan all podcast sources
  const handleScanAllSources = async (isEmergency: boolean = false) => {
    setIsScanningSources(true);
    try {
      if (!publisherId) {
        throw new Error('Publisher ID is missing.');
      }
      const websites = await getWebsitesByPublisher(publisherId);
      if (websites.length === 0) {
        throw new Error('No websites found for this publisher.');
      }
      const websiteId = websites[0].id;
      const newArticles = await generateArticlesFromMultiSource(websiteId, publisherId, isEmergency);
      
      if (newArticles && newArticles.length > 0) {
        dispatch(addArticles(newArticles));
        setSourceFeedbackSeverity('success');
        setSourceFeedbackMessage(`Successfully scanned sources and added ${newArticles.length} new articles.`);
      } else {
        setSourceFeedbackSeverity('info');
        setSourceFeedbackMessage('Scanned sources but found no new articles.');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to scan podcast sources';
      setSourceFeedbackSeverity('error');
      setSourceFeedbackMessage(errorMessage);
    } finally {
      setIsScanningSources(false);
    }
  };

  const allSelectableSourceKeys = SOURCE_BUTTONS
    .filter((s) => !ALWAYS_VISIBLE_SOURCE_KEYS.includes(s.key as typeof ALWAYS_VISIBLE_SOURCE_KEYS[number]))
    .map((s) => s.key);

  const handleSelectAllSources = () => {
    const next = Array.from(new Set([...visibleSourceKeys, ...allSelectableSourceKeys]));
    persistVisibleSources(next);
  };

  const handleSelectNoneSources = () => {
    const next = visibleSourceKeys.filter((key) => ALWAYS_VISIBLE_SOURCE_KEYS.includes(key as typeof ALWAYS_VISIBLE_SOURCE_KEYS[number]));
    persistVisibleSources(next);
  };

  type VisibleSource = {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  };

  // Build the runtime config for visible buttons here, AFTER all handlers are defined
  const visibleSources = visibleSourceKeys.map((key): VisibleSource | null => {
    switch (key) {
      case 'blank':
        return {
          key,
          label: 'Blank',
          icon: <AddIcon />,
          onClick: () => handleOpenForm(),
        };
      case 'from_url':
        return {
          key,
          label: 'From URL',
          icon: <LinkIcon />,
          onClick: handleOpenUrlDialog,
        };
      case 'from_pdf':
        return {
          key,
          label: isGeneratingFromPdf ? 'Importing PDF...' : 'From PDF',
          icon: isGeneratingFromPdf ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />,
          onClick: handleOpenPdfPicker,
          disabled: isGeneratingFromPdf,
        };
      case 'weather':
        return {
          key,
          label: 'Weather',
          icon: isGeneratingWeatherArticle ? <CircularProgress size={20} color="inherit" /> : <WeatherIcon />,
          onClick: handleGenerateWeatherArticle,
          disabled: isGeneratingWeatherArticle,
        };
      case 'events':
        return {
          key,
          label: 'Events',
          icon: <NotificationsActiveIcon />,
          onClick: handleEventsClick,
        };
      case 'from_rss_feed':
        return {
          key,
          label: 'From RSS Feed',
          icon: <RssFeedIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleOpenRssFeedDialog());
          },
        };
      case 'marin':
        return {
          key,
          label: 'Marin County',
          icon: <PublicIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleMarinCountyClick());
          },
        };
      case 'sonoma':
        return {
          key,
          label: 'Sonoma County',
          icon: <PublicIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSonomaCountyClick());
          },
        };
      case 'san_rafael':
        return {
          key,
          label: 'San Rafael',
          icon: <LocationCityIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSanRafaelClick());
          },
        };
      case 'santa_rosa':
        return {
          key,
          label: 'Santa Rosa',
          icon: <LocationCityIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSantaRosaClick());
          },
        };
      case 'petaluma':
        return {
          key,
          label: 'Petaluma',
          icon: <LocationCityIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handlePetalumaClick());
          },
        };
      case 'novato':
        return {
          key,
          label: 'Novato',
          icon: <LocationCityIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleNovatoClick());
          },
        };
      case 'mill_valley':
        return {
          key,
          label: 'Mill Valley',
          icon: <LocationCityIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleMillValleyClick());
          },
        };
      case '511':
        return {
          key,
          label: '511.org',
          icon: <TransitIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handle511Click());
          },
        };
      case 'ggbhtd':
        return {
          key,
          label: 'Golden Gate Bridge Hwy & Transit',
          icon: <TransitIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleGoldenGateBridgeDistrictClick());
          },
        };
      case 'smart':
        return {
          key,
          label: 'Sonoma Marin Area Rail Transit',
          icon: <TransitIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSmartClick());
          },
        };
      case 'caltrans4':
        return {
          key,
          label: 'Caltrans District 4 - State Highways',
          icon: <HighwayIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleCaltransDistrict4Click());
          },
        };
      case 'chp':
        return {
          key,
          label: 'CHP Incident Feeds',
          icon: <LocalPoliceIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleChpIncidentFeedsClick());
          },
        };
      case 'pge':
        return {
          key,
          label: 'PG&E - Outage Alerts & PSPS',
          icon: <NotificationsActiveIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handlePgeOutagesClick());
          },
        };
      case 'alert_marin':
        return {
          key,
          label: 'AlertMarin County Emergency Alerts',
          icon: <NotificationsActiveIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleAlertMarinClick());
          },
        };
      case 'soco_alert':
        return {
          key,
          label: 'SoCoAlert County Emergency Alerts',
          icon: <NotificationsActiveIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSoCoAlertClick());
          },
        };
      case 'nixle':
        return {
          key,
          label: 'Nixle Community Alerts',
          icon: <NotificationsActiveIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleNixleClick());
          },
        };
      case 'marin_oes':
        return {
          key,
          label: 'Marin County Sheriff - OES Portal',
          icon: <LocalPoliceIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleMarinOesPortalClick());
          },
        };
      case 'soco_emergency':
        return {
          key,
          label: 'SoCoEmergency',
          icon: <NotificationsActiveIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSoCoEmergencyClick());
          },
        };
      case 'nws_bay':
        return {
          key,
          label: 'National Weather Service Bay Area',
          icon: <WeatherIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleNwsBayAreaClick());
          },
        };
      case 'baaqmd':
        return {
          key,
          label: 'Bay Area Air Quality Mgmt District',
          icon: <PublicIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleBaaqmdClick());
          },
        };
      case 'marin_hhs':
        return {
          key,
          label: 'Marin County HHS - Public Health',
          icon: <LocalHospitalIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleMarinHhsClick());
          },
        };
      case 'sonoma_dhs':
        return {
          key,
          label: 'Sonoma County DHS - Public Health',
          icon: <LocalHospitalIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSonomaDhsClick());
          },
        };
      case 'sonoma_edb':
        return {
          key,
          label: 'Sonoma County Economic Development Board',
          icon: <BusinessIcon />,
          onClick: () => {
            setSourceFeedbackSeverity('info');
            setSourceFeedbackMessage(handleSonomaEdbClick());
          },
        };
      default:
        return null;
    }
  }).filter((s): s is VisibleSource => s !== null);

  return (
    <Box sx={{ width: '100%' }}>
      <CollapsibleSection
        title="Podcast Sources"
        icon={<RssFeedIcon />}
        namespace="articles-table-sources"
        actionButton={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleScanAllSources(true)}
              disabled={isScanningSources}
              startIcon={isScanningSources ? <CircularProgress size={20} color="inherit" /> : <WarningIcon />}
              size="small"
            >
              Create Emergency Podcast
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => handleScanAllSources(false)}
              disabled={isScanningSources}
              startIcon={isScanningSources ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
              size="small"
            >
              {isScanningSources ? 'Scanning Sources...' : 'Scan All Podcast Sources Now'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleOpenAddSourcesDialog}
              startIcon={<AddIcon />}
              size="small"
            >
              Add Podcast Sources
            </Button>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Click any of the source buttons below to quickly add articles from that source.
          </Typography>
          {isGeneratingFromPdf && (
            <Alert severity="info">
              Importing "{selectedPdfFileName || 'PDF'}" and creating articles. This can take a minute or two.
            </Alert>
          )}
          <input
            ref={pdfFileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            hidden
            onChange={handlePdfFileChange}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(140px, 1fr))',
                sm: 'repeat(3, minmax(150px, 1fr))',
                md: 'repeat(4, minmax(160px, 1fr))',
                lg: 'repeat(6, minmax(170px, 1fr))',
              },
            }}
          >
            {visibleSources.map((source) => (
              <Box
                key={source.key}
                sx={{
                  position: 'relative',
                  '&:hover .delete-icon': { opacity: 1 },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={source.onClick}
                  disabled={source.disabled}
                  sx={{
                    height: 80,
                    minWidth: 0,
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    textTransform: 'none',
                    borderRadius: 2,
                    gap: 0.5,
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {source.icon}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {source.label}
                  </Typography>
                </Button>
                {!ALWAYS_VISIBLE_SOURCE_KEYS.includes(source.key as typeof ALWAYS_VISIBLE_SOURCE_KEYS[number]) && (
                  <IconButton
                    size="small"
                    className="delete-icon"
                    onClick={(e) => handleRemoveVisibleSource(source.key, e)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      bgcolor: 'rgba(0,0,0,0.5)',
                      color: 'common.white',
                      p: 0.25,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.7)',
                      },
                    }}
                >
                  <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </CollapsibleSection>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ marginLeft: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
          <ArticleFilters publisherId={publisherId} />
        </Box>        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Headline</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'publishDate'}
                    direction={sortConfig.key === 'publishDate' ? sortConfig.direction : 'desc'}
                    onClick={() => handleRequestSort('publishDate')}
                  >
                    Published Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'status'}
                    direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'journalistName'}
                    direction={sortConfig.key === 'journalistName' ? sortConfig.direction : 'asc'}
                    onClick={() => handleRequestSort('journalistName')}
                  >
                    Journalist
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No articles found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((article) => (
                  <TableRow key={article.id} hover onClick={() => handleRowClick(article)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Typography variant="body1">
                        {truncateText(article.headline, 100)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {formatDate(article.publishDate)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {editingStatusId === article.id ? (
                        <Select
                          fullWidth
                          value={statusValue}
                          onChange={(e) => handleStatusChange(article, e.target.value)}
                          size="small"
                          autoFocus
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          label={article.status}
                          color={getStatusColor(article.status) as any}
                          size="small"
                          sx={{ cursor: 'pointer' }}
                          onClick={(event) => handleStatusClick(article, event)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {article.tags && article.tags.length > 0 ? (
                          article.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag.replace('_', ' ')}
                              color={getTagColor(index) as any}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No tags
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {article.journalistName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={(event) => handleDeleteClick(article, event)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={openConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${articleToDelete?.headline}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <ArticleForm
        open={openForm}
        onClose={handleCloseForm}
        article={selectedArticle}
        isEdit={isEdit}
        onSave={handleSaveArticle}
        publisherId={publisherId}
      />
      <Snackbar
        open={!!weatherArticleError}
        autoHideDuration={6000}
        onClose={() => setWeatherArticleError(null)}
      >
        <Alert onClose={() => setWeatherArticleError(null)} severity="error" sx={{ width: '100%' }}>
          {weatherArticleError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!sourceFeedbackMessage}
        autoHideDuration={6000}
        onClose={() => setSourceFeedbackMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSourceFeedbackMessage(null)}
          severity={sourceFeedbackSeverity}
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: 600,
            mx: 'auto',
            fontSize: '1rem',
            fontWeight: 600,
            py: 2,
            px: 3,
            boxShadow: 6,
            borderRadius: 2,
          }}
        >
          {sourceFeedbackMessage}
        </Alert>
      </Snackbar>

      {/* URL Input Dialog */}
      <Dialog open={urlDialogOpen} onClose={handleCloseUrlDialog} maxWidth="sm" fullWidth>
        <DialogTitle>New Article From URL</DialogTitle>
        <DialogContent>
          <TextField
            inputRef={urlInputRef}
            margin="dense"
            label="Article URL"
            type="url"
            fullWidth
            variant="outlined"
            value={articleUrl}
            onChange={(e) => setArticleUrl(e.target.value)}
            error={!!urlError}
            helperText={urlError}
            placeholder="https://example.com/article"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleGenerateFromUrl();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUrlDialog} disabled={isGeneratingFromUrl}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateFromUrl}
            variant="contained"
            color="primary"
            disabled={isGeneratingFromUrl}
            startIcon={isGeneratingFromUrl ? <CircularProgress size={20} /> : null}
          >
            {isGeneratingFromUrl ? 'Generating...' : 'Generate Article'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pdfDialogOpen} onClose={handleClosePdfDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Articles From PDF</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selected file: {selectedPdfFileName || 'No file selected'}
            </Typography>
            <TextField
              inputRef={pdfPublishDateInputRef}
              label="Publish Date"
              type="datetime-local"
              fullWidth
              value={pdfPublishDate}
              onChange={(e) => setPdfPublishDate(e.target.value)}
              disabled={isGeneratingFromPdf}
              error={!!pdfDialogError}
              helperText={pdfDialogError || 'This publish date will be applied to all imported articles.'}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePdfDialog} disabled={isGeneratingFromPdf}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateFromPdf}
            variant="contained"
            color="primary"
            disabled={isGeneratingFromPdf}
            startIcon={isGeneratingFromPdf ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
          >
            {isGeneratingFromPdf ? 'Importing PDF...' : 'Import PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Podcast Sources Dialog */}
      <Dialog open={addSourcesDialogOpen} onClose={handleCloseAddSourcesDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Customize Podcast Sources</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which Podcast Source buttons are visible on the dashboard. "Blank", "From URL", and "From PDF" are
            always available, and additional sources you select will appear as quick-add buttons.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button size="small" variant="text" onClick={handleSelectAllSources}>
              Select all
            </Button>
            <Button size="small" variant="text" onClick={handleSelectNoneSources}>
              Select none
            </Button>
          </Stack>

          <FormGroup>
            {SOURCE_BUTTONS.filter((s) => !ALWAYS_VISIBLE_SOURCE_KEYS.includes(s.key as typeof ALWAYS_VISIBLE_SOURCE_KEYS[number])).map((source) => (
              <FormControlLabel
                key={source.key}
                control={
                  <Checkbox
                    checked={visibleSourceKeys.includes(source.key)}
                    onChange={(e) => handleToggleSourceVisibility(source.key, e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {source.icon}
                    </Box>
                    <Typography variant="body2">{source.label}</Typography>
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddSourcesDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ArticlesTable;
